import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, CircularProgress, List, ListItem, ListItemText, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RecommendationTile from '../../Components/RecommendationTile';
import { useLocation } from 'react-router-dom';

function Recommendations() {
  const location = useLocation();
  const selectedFeatures = location.state?.selectedFeatures || [];

  const [bestPlanResults, setBestPlanResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxTileHeight, setMaxTileHeight] = useState(0);
  const tileRefs = useRef([]); // References for each RecommendationTile

  useEffect(() => {
    const fetchBestPlan = async () => {
      setLoading(true);
      try {
        const response = await fetch('http://127.0.0.1:5000/api/find_best_plan', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ selected_features: selectedFeatures }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch best plan');
        }

        const data = await response.json();
        setBestPlanResults(data);
      } catch (err) {
        console.error('Error fetching best plan:', err);
      } finally {
        setLoading(false);
      }
    };

    if (selectedFeatures.length > 0) {
      fetchBestPlan();
    } else {
      setLoading(false);
    }
  }, [selectedFeatures]);

  // Calculate maximum tile height after the component has rendered
  useEffect(() => {
    if (bestPlanResults.length > 0) {
      setTimeout(() => {
        const heights = tileRefs.current.map((ref) => ref?.offsetHeight || 0);
        setMaxTileHeight(Math.max(...heights));
      }, 0); // Wait until next render cycle for DOM measurements
    }
  }, [bestPlanResults]);

  return (
    <Box mt={4} textAlign="center">
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box position="relative" width="80%" margin="0 auto" mb={3}>
            <Typography variant="h4" sx={{ color: 'rgb(17, 63, 131)', textAlign: 'center' }}>
              Top Recommended Plans
            </Typography>
            {selectedFeatures.length > 0 && (
              <Accordion 
                sx={{ 
                  position: 'absolute', 
                  top: 0,               
                  right: 0,            
                  width: '300px',       
                  backgroundColor: '#f9f9f9', 
                  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)', 
                  borderRadius: '8px', 
                  zIndex: 10
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="selected-features-content" id="selected-features-header">
                  <Typography variant="h6" sx={{ color: 'rgb(17, 63, 131)' }}>Selected Features</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {selectedFeatures.map((feature, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={feature.title} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>

          {/* Render Recommendation Tiles */}
          {bestPlanResults.length > 0 ? (
            <Box display="flex" flexWrap="wrap" justifyContent="center" gap={3} mt={2}>
              {bestPlanResults.map((plan, idx) => (
                <div
                  key={idx}
                  ref={(el) => (tileRefs.current[idx] = el)} // Set reference for each tile
                >
                  <RecommendationTile 
                    planName={plan.Product_Display_Name} 
                    monthlyPrice={plan.Monthly_Price} 
                    yearlyPrice={plan.Yearly_Price} 
                    skuDescription={plan.SkuDescription}
                    includedServices={plan.Included_Services}
                    notIncludedServices={plan.Not_Included_Services}
                    maxHeight={maxTileHeight} // Pass calculated max height to each tile
                  />
                </div>
              ))}
            </Box>
          ) : (
            <Typography variant="h6" mt={3}>No recommendations available.</Typography>
          )}
        </>
      )}
    </Box>
  );
}

export default Recommendations;
